import cn from 'classnames';
import { useComponentInViewport } from 'core-lib/hooks/useComponentInViewport';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'ui-v2/components/Button';
import { Typography } from 'ui-v2/components/Typography';
import useStoreItemClick from '../../../hooks/useStoreItemClick';
import { LayoutEntityType, LayoutPropType } from '../../../types/layout';
import {
  trackCardClickedStoreV2,
  trackCtaClickedStoreV2,
  trackSectionViewedStoreV2,
} from '../../../utils/events';
import ProductCard from '../../Cards/ProductCard';
import s from './FilterListing.module.scss';
import FuelDrop from 'src/modules/parkplusFuel/icons/fuelDrop';
import Icon from 'ui-v2/components/Icon/Icon';

const parentIdMap = {};

export const FilterListing = ({ data }: LayoutPropType) => {
  // state
  const [activeTab, setActiveTab] = useState(0);

  const { handleItemClick, handleItemClickWithDelegation } =
    useStoreItemClick();
  const CARDID = data.entityType;

  //const
  const rootRef = useRef();
  const isEventFired = useRef(false);
  const inViewPort = useComponentInViewport(rootRef, {
    threshold: 0.5,
  });

  useEffect(() => {
    if (inViewPort && !isEventFired.current) {
      trackSectionViewedStoreV2(data?.title);
      isEventFired.current = true;
    }
  }, [data?.title, inViewPort]);

  useMemo(() => {
    data?.metaData?.categories.forEach((item) => {
      parentIdMap[item?.productId] = [];
    });
    data?.data?.forEach((item) => {
      item?.parentId && parentIdMap?.[item?.parentId]?.push(item);
    });
  }, [data]);

  const getIndex = (id: number) =>
    data.data.findIndex((item) => item.productId === id);

  const handleTabClick = (idx) => {
    setActiveTab(idx);
    const ele = document.getElementById(`${data?.layoutId}-${idx}-tab`);
    if (ele) {
      const timeout = setTimeout(() => {
        ele.scrollIntoView({
          block: 'nearest',
          inline: 'center',
          behavior: 'smooth',
        });
        clearTimeout(timeout);
      }, 100);
    }
    trackCardClickedStoreV2(data?.title, idx + 1);
  };

  return (
    <div
      className={s.root}
      ref={rootRef}
      onClick={(e) =>
        handleItemClickWithDelegation(
          e,
          CARDID,
          LayoutEntityType.Product,
          data?.data,
          data?.title
        )
      }
    >
      <div className={s.title}>
        <Typography label={data?.title} variant="label_1_bold" />
      </div>
      <div className={cn(s.tabBar, 'cmn_hide_scrollbar')}>
        {data?.metaData?.categories?.map((item, idx) => (
          <div
            key={`${idx}^&*()`}
            onClick={() => {
              handleTabClick(idx);
            }}
            className={cn(s.tab, 'cmn_touchable_area', {
              [s.activeTab]: idx === activeTab,
            })}
            id={`${data?.layoutId}-${idx}-tab`}
          >
            <div className={s.subtitle1}>
              {data?.metaData?.showDrop && idx === activeTab && (
                <FuelDrop width={16} height={16} />
              )}
              <Typography
                label={`Under ${item!.subtitle1}`}
                variant="label_3_medium"
                color={idx == activeTab ? 'on_dark_bg' : 'primary'}
              />
            </div>
          </div>
        ))}
      </div>
      <div className={s.listWrapper}>
        <div className={cn(s.cardList, 'cmn_hide_scrollbar')}>
          {parentIdMap?.[data?.metaData?.categories?.[activeTab]?.productId]
            ?.slice(0, 6)
            .map((item, index) => (
              <div className={s.cardWrapper} key={`${item?.id}`}>
                <ProductCard
                  product={item}
                  isSmall
                  section={data?.title}
                  position={getIndex(item.productId)}
                  id={CARDID}

                />
              </div>
            ))}
        </div>
      </div>
      <div className={s.view}>
        <Button
          label="View more deals"
          variant="secondary_color"
          rightIcon={
            <Icon name="ChevronRightRegular" color='brand' width={16} height={16} />
          }
          size="small"
          onClick={() => {
            trackCtaClickedStoreV2(
              data?.title,
              'view_more_deals',
              activeTab + 1
            );
            const activeCat = data?.metaData?.categories?.[activeTab];
            handleItemClick(
              data?.entityType,
              activeCat?.productId,
              activeCat?.filter
            );
          }}
        />
      </div>
    </div>
  );
};
